import { useCallback, useEffect, useRef, useState } from "react";
import app from "nystem";
import { ContentTypeView, SessionRole, Inserter } from "nystem-components";
import { CastContext } from "../../client/castContext.js";
import CastLocation from "./location.js";

const CastProvider = ({ children }) => {
  const [state, setState] = useState({});
  const [hd, setHd] = useState(() => localStorage.getItem("mediaqueueCastHD") !== "false");
  const [error, setError] = useState();
  const [subtitles, setSubtitles] = useState(false);
  const [controlsRequested, setControlsRequested] = useState(false);
  const [airPlayRequest, setAirPlayRequest] = useState(0);
  const [browserPlayback, setBrowserPlayback] = useState(false);
  const [browserWireless, setBrowserWireless] = useState(false);
  const [browserReady, setBrowserReady] = useState(false);
  const [browserPlayer, setBrowserPlayer] = useState();
  const media = useRef();
  const airPlaySupported =
    !!state.nativePlaybackAvailable ||
    typeof document.createElement("video").webkitShowPlaybackTargetPicker === "function";
  const registerAirPlayMedia = useCallback((video) => {
    media.current = video;
    const ready = () => setBrowserReady(video.readyState > 0);
    const wireless = () => setBrowserWireless(!!video.webkitCurrentPlaybackTargetIsWireless);
    video.addEventListener("loadedmetadata", ready);
    video.addEventListener("emptied", ready);
    video.addEventListener("webkitcurrentplaybacktargetiswirelesschanged", wireless);
    ready();
    wireless();
    return () => {
      video.removeEventListener("loadedmetadata", ready);
      video.removeEventListener("emptied", ready);
      video.removeEventListener("webkitcurrentplaybacktargetiswirelesschanged", wireless);
      if (media.current === video) media.current = undefined;
      setBrowserReady(false);
      setBrowserWireless(false);
    };
  }, []);
  const request = useCallback(async (op, args = {}) => {
    if (window.mediaqueueNative) return window.mediaqueueNative.request(op, args);
    if (op === "display.player") setBrowserPlayer(args.video ? args : undefined);
    if (op === "display.clear") setBrowserPlayer(undefined);
    return true;
  }, []);
  const beginAirPlay = () => {
    setError(undefined);
    if (!airPlaySupported) {
      setError(
        window.mediaqueueNative
          ? "AirPlay is not ready. Close and reopen the iPhone app, then try again."
          : "Open MediaQueue in Safari or the iPhone app to use AirPlay.",
      );
      return;
    }
    if (state.nativePlayback) request("airplay.stop").catch((error) => setError(error.message));
    if (state.nativePlayback || browserWireless) {
      stopBrowserPlayback();
      return;
    }
    setControlsRequested(true);
    if (state.nativePlaybackAvailable) {
      request("airplay.show").catch((error) => setError(error.message));
      setAirPlayRequest((value) => value + 1);
    } else if (media.current?.readyState > 0) {
      // Safari requires the picker to open directly in the click handler.
      try {
        media.current.webkitShowPlaybackTargetPicker();
      } catch (error) {
        setError(error.message);
      }
    } else {
      setBrowserPlayback(true);
    }
  };
  const stopBrowserPlayback = () => {
    setBrowserPlayback(false);
    setBrowserPlayer(undefined);
    setControlsRequested(false);
  };
  const feedback = useCallback((data) => {
    window.dispatchEvent(new CustomEvent("mediaqueuePlayback", { detail: data }));
  }, []);
  const effectiveState = {
    ...state,
    connected: !!state.connected || browserPlayback,
    browserWireless,
    browserPlayback,
  };
  const casting = effectiveState.connected || controlsRequested;
  const context = {
    state: effectiveState,
    casting,
    hd,
    setHd,
    subtitles,
    setSubtitles,
    request,
    airPlaySupported,
    beginAirPlay,
    airPlayRequest,
    browserPlayback,
    stopBrowserPlayback,
  };

  useEffect(() => localStorage.setItem("mediaqueueCastHD", String(hd)), [hd]);

  useEffect(() => {
    if (!window.mediaqueueNative) return;
    let active = true;
    const changed = ({ detail }) => {
      setState(detail);
      if (detail.nativePlayback) setAirPlayRequest(0);
    };
    const logout = () => window.mediaqueueNative.request("display.clear").catch((error) => setError(error.message));
    window.addEventListener("mediaqueueCast", changed);
    app.on("logout", logout);
    window.mediaqueueNative
      .request("display.status")
      .then((state) => {
        if (active) setState(state);
      })
      .catch((error) => active && setError(error.message));
    return () => {
      active = false;
      window.removeEventListener("mediaqueueCast", changed);
      app.off("logout", logout);
    };
  }, []);

  return (
    <CastContext.Provider value={context}>
      {children}
      <Inserter match="*" exclude="/cast">
        <Inserter match="*" exclude={casting ? undefined : "/video/player"}>
          <SessionRole userrole="logged-in">
            <CastLocation claim={casting} className="fixed inset-x-0 bottom-0 z-[100]">
              <ContentTypeView contentType="video" view={app.populatedViews.mediaqueueCast.views.controls} />
            </CastLocation>
          </SessionRole>
        </Inserter>
      </Inserter>
      {browserPlayback && (
        <>
          <div className="fixed bottom-60 right-4 z-[90] aspect-video w-64 overflow-hidden rounded-lg bg-black">
            {browserPlayer?.video && (
              <CastContext.Provider
                value={{
                  ...context,
                  hd: browserPlayer.hd,
                  subtitles: browserPlayer.subtitles,
                  receiver: true,
                  registerAirPlayMedia,
                  feedback,
                }}
              >
                <ContentTypeView
                  key={browserPlayer.video._id}
                  contentType="video"
                  view={app.populatedViews.mediaqueueCast.views.browser}
                  value={browserPlayer.video}
                />
              </CastContext.Provider>
            )}
          </div>
          <p role="status" className="fixed bottom-52 inset-x-4 z-[100] text-center text-sm text-white">
            {browserWireless
              ? "Playing with AirPlay"
              : browserReady
                ? "Click the AirPlay icon again to choose your TV."
                : "Preparing AirPlay…"}
          </p>
        </>
      )}
      {(error || state.error) && (
        <p role="alert" className="fixed bottom-24 inset-x-4 z-[100] bg-black p-4 text-red-300">
          {error || state.error}
        </p>
      )}
    </CastContext.Provider>
  );
};

export default CastProvider;
